import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { collection, onSnapshot, doc, updateDoc, serverTimestamp } from 'firebase/firestore';

// ---------------------------------------------------------------------------
// Client Dashboard — review & publish (client profile card)
//
// The review gate (client-dashboard-brief.md §4). Each assembled month shows
// as a draft here with what it is still waiting on; staff read the recap
// (editable), open "View as client" to check the numbers, then Publish. Publish
// freezes the month for the client and emails every active dashboard user.
// Unpublish puts it back to draft (logged). Optional auto-publish: set hours
// and drafts publish themselves once complete and older than that.
// ---------------------------------------------------------------------------

const BASE = 'https://us-central1-core6-marketing.cloudfunctions.net';
const DASHBOARD = 'https://dashboard.core6.marketing';

async function api(path, body) {
  const token = await auth.currentUser.getIdToken();
  const r = await fetch(`${BASE}/${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
  return data;
}
const fmt = ts => (ts?.toDate ? ts.toDate().toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');

function Status({ status }) {
  const on = status === 'published';
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '.4px', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 4, whiteSpace: 'nowrap',
      ...(on ? { background: 'var(--text-primary)', color: 'var(--bg-primary, #fff)', border: '1px solid var(--text-primary)' } : { background: 'transparent', color: 'var(--text-muted)', border: '1px dashed var(--text-muted)' }),
    }}>{on ? '● Published' : '○ Draft'}</span>
  );
}

// "What Core6 is doing next" editor (functions-dashboard/lib/focus.js). The
// state and headline come from the share-of-demand rule; staff can reword
// the headline, rewrite the one to three commitments, and mark what happened
// to last month's. Saving marks the focus as staff-edited so a reassembly
// never overwrites it.
const STATE_WORDS = { up: 'Trending up', steady: 'Holding steady', focus: 'Extra focus', seasonal: 'Seasonal dip', first: 'First month', small: 'Early days' };
const LAST_OPTIONS = [['pending', 'No update yet'], ['done', 'Done'], ['in_progress', 'In progress'], ['not_done', 'Not done']];
const inputStyle = { fontSize: 12, padding: '5px 8px', border: '1px solid var(--border-color, #d6d9de)', borderRadius: 6, boxSizing: 'border-box' };

function FocusEditor({ m, clientId, busy, run }) {
  const f = m.focus || null;
  const [headline, setHeadline] = useState(f?.headline || '');
  const [items, setItems] = useState(f?.items || []);
  const [last, setLast] = useState(f?.lastMonth || []);
  useEffect(() => { setHeadline(f?.headline || ''); setItems(f?.items || []); setLast(f?.lastMonth || []); }, [m.id, m.focusSource, m.focusEditedAt]);
  if (!f && m.status !== 'draft') return null;
  const dirty = JSON.stringify({ headline, items, last }) !== JSON.stringify({ headline: f?.headline || '', items: f?.items || [], last: f?.lastMonth || [] });
  const setItem = (i, k, v) => setItems(list => list.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const setLastRow = (i, k, v) => setLast(list => list.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const pending = last.filter(x => !x.status || x.status === 'pending').length;
  const demand = f?.demand;
  return (
    <div style={{ marginTop: 12, padding: '10px 12px', border: '1px solid var(--border-color, #e3e6ea)', borderRadius: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.3px', color: 'var(--text-muted)' }}>
          What Core6 is doing next {m.focusSource === 'staff' ? '(edited by staff)' : m.focusSource === 'ai' ? '(generated)' : '(none yet)'}
        </label>
        {f && <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', border: '1px solid var(--text-primary)', borderRadius: 999 }}>{STATE_WORDS[f.state] || f.state}</span>}
        {f && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {f.leads?.cur != null && f.leads?.prev != null ? `inquiries ${f.leads.cur} vs ${f.leads.prev}` : 'no prior month'}
          {demand ? ` · demand (${demand.source === 'gbp_search' ? 'Profile searches' : 'search impressions'}) ${demand.cur} vs ${demand.prev}${demand.pct != null ? ` (${demand.pct > 0 ? '+' : ''}${demand.pct}%)` : ''}` : ' · no demand signal'}
          {f.reason ? ` · ${f.reason}` : ''}
        </span>}
      </div>
      {!f && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>Generated when the month assembles. Add items below to write it by hand.</div>}
      <input value={headline} onChange={e => setHeadline(e.target.value)} placeholder="Headline the client reads first" style={{ ...inputStyle, width: '100%', marginTop: 8, fontWeight: 600 }} />
      <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
        {items.map((it, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 2fr) minmax(200px, 3fr) 90px auto', gap: 6, alignItems: 'center' }}>
            <input value={it.title || ''} onChange={e => setItem(i, 'title', e.target.value)} placeholder="What Core6 will do (verb first)" style={inputStyle} />
            <input value={it.detail || ''} onChange={e => setItem(i, 'detail', e.target.value)} placeholder="Why, in one sentence" style={inputStyle} />
            <input value={it.due || ''} onChange={e => setItem(i, 'due', e.target.value)} placeholder="by Nov 12" style={inputStyle} />
            <button type="button" className="btn btn-secondary" style={{ fontSize: 11 }} onClick={() => setItems(list => list.filter((_, j) => j !== i))}>Remove</button>
          </div>
        ))}
        {items.length < 3 && <button type="button" className="btn btn-secondary" style={{ fontSize: 11, justifySelf: 'start' }} onClick={() => setItems(list => [...list, { title: '', detail: '', due: '' }])}>+ Add item</button>}
      </div>
      {last.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.3px', color: 'var(--text-muted)' }}>
            Last month we said {pending > 0 && m.status === 'draft' ? <span style={{ color: 'var(--text-primary)' }}>· {pending} still unmarked, set each before publishing</span> : null}
          </div>
          <div style={{ display: 'grid', gap: 6, marginTop: 6 }}>
            {last.map((x, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '130px minmax(160px, 2fr) minmax(160px, 2fr)', gap: 6, alignItems: 'center', fontSize: 12 }}>
                <select value={x.status || 'pending'} onChange={e => setLastRow(i, 'status', e.target.value)} style={inputStyle}>{LAST_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                <span style={{ fontWeight: 600 }}>{x.title}</span>
                <input value={x.note || ''} onChange={e => setLastRow(i, 'note', e.target.value)} placeholder="Short note the client sees (optional)" style={inputStyle} />
              </div>
            ))}
          </div>
        </div>
      )}
      <div style={{ marginTop: 8 }}>
        <button type="button" className="btn btn-secondary" style={{ fontSize: 12 }} disabled={!!busy || !dirty || !items.some(x => (x.title || '').trim().length >= 4)}
          onClick={() => run(`focus-${m.id}`, () => api('dashboardSaveFocus', { viewAs: clientId, month: m.id, headline: headline.trim() || undefined, items, lastMonth: last }), 'Next-month plan saved.')}>Save plan</button>
      </div>
    </div>
  );
}

export default function DashboardPublishPanel({ clientId, client }) {
  const [open, setOpen] = useState(false);
  const [months, setMonths] = useState([]);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const [autoHours, setAutoHours] = useState(client?.dashboardSources?.autoPublishHours || '');

  useEffect(() => { setAutoHours(client?.dashboardSources?.autoPublishHours || ''); }, [client?.dashboardSources?.autoPublishHours]);

  useEffect(() => {
    if (!open) return undefined;
    const unsub = onSnapshot(collection(db, 'clients', clientId, 'snapshots'), snap => {
      setMonths(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => b.id.localeCompare(a.id)));
    }, () => setMonths([]));
    return () => unsub();
  }, [open, clientId]);

  const run = async (label, fn, okText) => {
    setBusy(label); setMsg(null);
    try { const out = await fn(); setMsg({ ok: true, text: typeof okText === 'function' ? okText(out) : okText }); }
    catch (err) { setMsg({ ok: false, text: err.message || 'Request failed.' }); }
    finally { setBusy(null); }
  };

  const saveAuto = async () => {
    const hours = autoHours === '' ? null : Math.max(0, Number(autoHours) || 0);
    await updateDoc(doc(db, 'clients', clientId), { 'dashboardSources.autoPublishHours': hours || null, 'dashboardSources.updatedAt': serverTimestamp(), 'dashboardSources.updatedBy': auth.currentUser?.email || null });
    return hours ? `Drafts will publish themselves ${hours}h after they are complete.` : 'Auto-publish is off; every month waits for your approval.';
  };

  return (
    <div className="card" style={{ marginTop: 20 }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div className="card-title" style={{ marginBottom: 0 }}>Client Dashboard Review &amp; Publish</div>
          {open && <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>{months.filter(m => m.status === 'draft').length} draft · {months.filter(m => m.status === 'published').length} published</span>}
        </div>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>{open ? '▾ Hide' : '▸ Show'}</span>
      </div>

      {open && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14, fontSize: 12 }}>
            <span style={{ fontWeight: 700 }}>Auto-publish drafts after</span>
            <input type="number" min={0} max={240} value={autoHours} onChange={e => setAutoHours(e.target.value)} placeholder="off" style={{ width: 70, padding: '5px 8px', fontSize: 12 }} />
            <span>hours</span>
            <button type="button" className="btn btn-secondary" style={{ fontSize: 12 }} disabled={!!busy} onClick={() => run('auto', saveAuto, r => r)}>Save</button>
            <span style={{ color: 'var(--text-muted)' }}>Blank = wait for your approval every month.</span>
          </div>

          {msg && <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10 }}>{msg.ok ? '✓ ' : '✕ '}{msg.text}</div>}
          {months.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No months assembled yet.</div>}

          {months.map(m => {
            const missing = m.missingSources || [];
            const unclassified = m.summary?.leads?.unclassified || 0;
            const ready = m.status === 'draft' && !missing.length && !unclassified;
            const recap = edits[m.id] ?? (m.recap || '');
            return (
              <div key={m.id} style={{ borderTop: '1px solid var(--border-color, #e3e6ea)', padding: '12px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
                  <strong style={{ fontSize: 14 }}>{m.periodLabel || m.id}</strong>
                  <Status status={m.status} />
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>built {fmt(m.assembledAt)}{m.status === 'published' ? ` · published ${fmt(m.publishedAt)} by ${m.publishedBy || '—'}` : ''}</span>
                  <a href={`${DASHBOARD}/?viewAs=${encodeURIComponent(clientId)}&month=${m.id}`} target="_blank" rel="noreferrer" style={{ fontSize: 12, marginLeft: 'auto' }}>View as client ↗</a>
                </div>
                {m.status === 'draft' && (missing.length > 0 || unclassified > 0) && (
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                    Waiting on: {[...missing, ...(unclassified ? [`${unclassified} unclassified contact${unclassified === 1 ? '' : 's'}`] : [])].join(', ')}
                  </div>
                )}
                {(m.pendingSources || []).length > 0 && (
                  <div style={{ fontSize: 12, marginBottom: 6 }}>
                    Early view: {m.pendingSources.join(', ')} fill in by {m.pendingBy || 'their pull day'}{m.status === 'published' ? ' (stays published, no new email)' : ' (can publish now)'}.
                  </div>
                )}
                {m.summary?.leads && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
                    {m.summary.leads.total} new customer inquiries · {m.summary.calls?.total ?? '—'} calls · {m.summary.forms?.legitimate ?? '—'} forms · {m.summary.reviews?.newCount ?? '—'} reviews
                    {m.summary.leads.lowConfidence ? ` · ${m.summary.leads.lowConfidence} low-confidence to review` : ''}
                  </div>
                )}
                <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.3px', color: 'var(--text-muted)' }}>
                  Recap {m.recapSource === 'staff' ? '(edited by staff)' : m.recapSource === 'ai' ? '(generated)' : '(none yet)'}
                </label>
                <textarea value={recap} onChange={e => setEdits(s => ({ ...s, [m.id]: e.target.value }))} rows={3}
                  style={{ width: '100%', boxSizing: 'border-box', fontSize: 13, padding: 8, border: '1px solid var(--border-color, #d6d9de)', borderRadius: 6, marginTop: 4 }} />
                <FocusEditor m={m} clientId={clientId} busy={busy} run={run} />
                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  <button type="button" className="btn btn-secondary" style={{ fontSize: 12 }} disabled={!!busy || recap === (m.recap || '')}
                    onClick={() => run(`recap-${m.id}`, () => api('dashboardSaveRecap', { viewAs: clientId, month: m.id, recap }), 'Recap saved.')}>Save recap</button>
                  {m.status === 'draft' ? (
                    <button type="button" className="btn btn-primary" style={{ fontSize: 12 }} disabled={!!busy || !ready}
                      onClick={() => { if (!window.confirm(`Publish ${m.periodLabel || m.id}? The month becomes visible to this client's dashboard users.`)) return; run(`pub-${m.id}`, () => api('dashboardPublish', { viewAs: clientId, month: m.id }), r => (r.emailsOn ? `Published. ${r.emailed} user${r.emailed === 1 ? '' : 's'} emailed.` : 'Published. Client emails are off, so nobody was notified.')); }}>
                      {busy === `pub-${m.id}` ? 'Publishing…' : 'Publish'}
                    </button>
                  ) : (
                    <button type="button" className="btn btn-secondary" style={{ fontSize: 12 }} disabled={!!busy}
                      onClick={() => { const reason = window.prompt('Why is this month being unpublished? (logged)'); if (reason === null) return; run(`unpub-${m.id}`, () => api('dashboardUnpublish', { viewAs: clientId, month: m.id, reason }), 'Back to draft. Republish when corrected.'); }}>
                      Unpublish
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
